import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  ImportState,
  ImportActions,
  ImportFileInfo,
  DEFAULT_IMPORT_SETTINGS,
  detectAssetType,
} from '../../core/types/import.types';
import { useProjectPanelStore } from './projectPanelStore';

const generateId = () => Math.random().toString(36).substring(2, 11);

interface ImportStore extends ImportState, ImportActions {}

export const useImportStore = create<ImportStore>()(
  persist(
    immer((set, get) => ({
      // Initial state
      files: [],
      settings: DEFAULT_IMPORT_SETTINGS,
      isImporting: false,
      progress: {
        total: 0,
        completed: 0,
        failed: 0,
        skipped: 0,
        currentProgress: 0,
      },
      results: [],
      isOpen: false,
      selectedFileId: null,
      targetFolderId: 'assets',
      sortBy: 'name',
      sortOrder: 'asc',

      // Dialog control
      openDialog: (targetFolderId) => {
        set((state) => {
          state.isOpen = true;
          if (targetFolderId) {
            state.targetFolderId = targetFolderId;
          }
          state.files = [];
          state.results = [];
          state.progress = {
            total: 0,
            completed: 0,
            failed: 0,
            skipped: 0,
            currentProgress: 0,
          };
        });
      },

      closeDialog: () => {
        set((state) => {
          state.isOpen = false;
          state.files = [];
          state.selectedFileId = null;
        });
      },

      // File management
      addFiles: (files) => {
        const fileArray = Array.from(files);

        set((state) => {
          for (const file of fileArray) {
            // Check for duplicates
            const exists = state.files.some(f => f.name === file.name && f.size === file.size);
            if (exists) continue;

            const type = detectAssetType(file);
            const id = generateId();

            const fileInfo: ImportFileInfo = {
              id,
              file,
              name: file.name,
              extension: '.' + file.name.split('.').pop()?.toLowerCase(),
              size: file.size,
              type,
              mimeType: file.type,
              targetFolder: state.targetFolderId,
              assetType: type,
              status: 'pending',
              progress: 0,
              isSelected: true,
              isSpriteSheet: false,
              shouldCompress: state.settings.compressImages && type === 'image',
              shouldGenerateThumbnail: state.settings.generateThumbnails && type === 'image',
            };

            state.files.push(fileInfo);

            // Generate preview for images
            if (type === 'image') {
              const reader = new FileReader();
              reader.onload = (e) => {
                const previewUrl = e.target?.result as string;
                get().updateFile(id, { previewUrl });

                // Get image dimensions
                const img = new Image();
                img.onload = () => {
                  get().updateFile(id, {
                    dimensions: { width: img.width, height: img.height },
                    isSpriteSheet:
                      state.settings.autoDetectSpriteSheets &&
                      img.width >= state.settings.spriteSheetThreshold.width &&
                      img.height >= state.settings.spriteSheetThreshold.height,
                  });
                };
                img.src = previewUrl;
              };
              reader.readAsDataURL(file);
            }
          }

          state.progress.total = state.files.filter(f => f.isSelected).length;
        });
      },

      removeFile: (fileId) => {
        set((state) => {
          const index = state.files.findIndex(f => f.id === fileId);
          if (index >= 0) {
            // Revoke preview URL to free memory
            const file = state.files[index];
            if (file.previewUrl) {
              URL.revokeObjectURL(file.previewUrl);
            }
            state.files.splice(index, 1);
          }
          if (state.selectedFileId === fileId) {
            state.selectedFileId = null;
          }
        });
      },

      clearFiles: () => {
        set((state) => {
          // Revoke all preview URLs
          for (const file of state.files) {
            if (file.previewUrl) {
              URL.revokeObjectURL(file.previewUrl);
            }
          }
          state.files = [];
          state.selectedFileId = null;
        });
      },

      selectFile: (fileId) => {
        set((state) => {
          state.selectedFileId = fileId;
        });
      },

      toggleFileSelected: (fileId) => {
        set((state) => {
          const file = state.files.find(f => f.id === fileId);
          if (file) {
            file.isSelected = !file.isSelected;
            state.progress.total = state.files.filter(f => f.isSelected).length;
          }
        });
      },

      selectAll: () => {
        set((state) => {
          for (const file of state.files) {
            file.isSelected = true;
          }
          state.progress.total = state.files.length;
        });
      },

      deselectAll: () => {
        set((state) => {
          for (const file of state.files) {
            file.isSelected = false;
          }
          state.progress.total = 0;
        });
      },

      // File configuration
      updateFile: (fileId, updates) => {
        set((state) => {
          const file = state.files.find(f => f.id === fileId);
          if (file) {
            Object.assign(file, updates);
          }
        });
      },

      setFileTargetFolder: (fileId, folderId) => {
        set((state) => {
          const file = state.files.find(f => f.id === fileId);
          if (file) {
            file.targetFolder = folderId;
          }
        });
      },

      setFileAssetType: (fileId, type) => {
        set((state) => {
          const file = state.files.find(f => f.id === fileId);
          if (file) {
            file.assetType = type;
          }
        });
      },

      setFileName: (fileId, name) => {
        set((state) => {
          const file = state.files.find(f => f.id === fileId);
          if (file) {
            file.customName = name;
          }
        });
      },

      // Settings
      updateSettings: (settings) => {
        set((state) => {
          state.settings = { ...state.settings, ...settings };
        });
      },

      resetSettings: () => {
        set((state) => {
          state.settings = DEFAULT_IMPORT_SETTINGS;
        });
      },

      // Import operations
      startImport: async () => {
        const state = get();
        const selectedFiles = state.files.filter(f => f.isSelected && f.status === 'pending');

        if (selectedFiles.length === 0) return;

        set((s) => {
          s.isImporting = true;
          s.progress = {
            total: selectedFiles.length,
            completed: 0,
            failed: 0,
            skipped: 0,
            currentProgress: 0,
          };
          s.results = [];
        });

        // Collect successfully imported files to add to project panel
        const importedFiles: { file: File; targetFolder: string }[] = [];

        for (const file of selectedFiles) {
          // Update current file
          set((s) => {
            s.progress.currentFile = file.name;
            const f = s.files.find(x => x.id === file.id);
            if (f) f.status = 'processing';
          });

          try {
            // Simulate import process (in real implementation, would use IPC)
            await new Promise(resolve => setTimeout(resolve, 200 + Math.random() * 300));

            // Determine final file name
            const finalName = file.customName ||
              (state.settings.keepOriginalNames ? file.name : `${generateId()}${file.extension}`);

            // Determine target path
            const targetPath = `${file.targetFolder}/${finalName}`;

            // Track successfully imported file for adding to project panel
            // Create a new File object with the final name if it was customized
            const importedFile = file.customName
              ? new File([file.file], finalName, { type: file.file.type })
              : file.file;
            importedFiles.push({
              file: importedFile,
              targetFolder: file.targetFolder,
            });

            // Update file status
            set((s) => {
              const f = s.files.find(x => x.id === file.id);
              if (f) {
                f.status = 'complete';
                f.progress = 100;
              }
              s.progress.completed += 1;
              s.progress.currentProgress = (s.progress.completed / s.progress.total) * 100;
              s.results.push({
                fileId: file.id,
                success: true,
                assetPath: targetPath,
              });
            });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';

            set((s) => {
              const f = s.files.find(x => x.id === file.id);
              if (f) {
                f.status = 'error';
                f.error = errorMessage;
              }
              s.progress.failed += 1;
              s.results.push({
                fileId: file.id,
                success: false,
                error: errorMessage,
              });
            });
          }
        }

        // Add successfully imported files to project panel store
        if (importedFiles.length > 0) {
          // Group files by target folder
          const filesByFolder: Record<string, File[]> = {};
          for (const { file, targetFolder } of importedFiles) {
            if (!filesByFolder[targetFolder]) {
              filesByFolder[targetFolder] = [];
            }
            filesByFolder[targetFolder].push(file);
          }

          // Import files to each folder
          const projectPanelStore = useProjectPanelStore.getState();
          for (const folderId of Object.keys(filesByFolder)) {
            projectPanelStore.importExternalFiles(filesByFolder[folderId], folderId);
          }
        }

        set((s) => {
          s.isImporting = false;
        });
      },

      cancelImport: () => {
        set((state) => {
          state.isImporting = false;
          // Mark processing files as pending again
          for (const file of state.files) {
            if (file.status === 'processing') {
              file.status = 'pending';
              file.progress = 0;
            }
          }
        });
      },

      retryFailed: async () => {
        set((state) => {
          for (const file of state.files) {
            if (file.status === 'error') {
              file.status = 'pending';
              file.progress = 0;
              file.error = undefined;
            }
          }
        });
        await get().startImport();
      },

      // Sorting
      setSortBy: (sortBy) => {
        set((state) => {
          state.sortBy = sortBy;
        });
      },

      setSortOrder: (order) => {
        set((state) => {
          state.sortOrder = order;
        });
      },
    })),
    {
      name: 'import-store',
      partialize: (state) => ({
        settings: state.settings,
      }),
    }
  )
);
